<template>
  <div class="customizable-radio-button" :class="containerClasses">
    <input
      :id="id"
      class="input"
      type="radio"
      :checked="isSelected"
      :disabled="disabled"
      :name="name"
      :value="value"
      @change="handleChange"
    />
    <label :for="id" class="radio-label">
      <!-- Bande colorée à gauche -->
      <div class="accent-band" />

      <!-- Cercle radio -->
      <div class="radio-circle">
        <!-- Point de sélection (visible seulement si sélectionné) -->
        <div v-if="isSelected" class="radio-dot" />
      </div>

      <!-- Contenu principal -->
      <span class="content">
        <span v-if="title" class="title">{{ title }}</span>
        <span v-if="description" class="description">{{ description }}</span>

        <!-- Slot pour contenu personnalisé -->
        <div v-if="$slots.content" class="slot-content">
          <slot name="content" />
        </div>

        <!-- Slot par défaut -->
        <div v-if="$slots.default" class="slot-default">
          <slot />
        </div>
      </span>

      <!-- Slot pour contenu à droite (ex: icône, badge) -->
      <span v-if="$slots.right" class="right-container">
        <slot name="right" />
      </span>
    </label>
  </div>
</template>

<script>
/**
 * Composant de bouton radio personnalisable avec bande colorée et slots
 * Supporte v-model avec la syntaxe Vue 3 (modelValue/update:modelValue)
 */
export default {
  name: 'BoutonRadioPersonnalise',
  props: {
    // ID unique pour l'input radio
    id: {
      type: String,
      required: true,
    },
    // Valeur du modèle (v-model)
    modelValue: {
      type: [String, Number],
      required: true,
    },
    // Valeur de ce bouton radio
    value: {
      type: [String, Number],
      required: true,
    },
    // Nom du groupe de boutons radio
    name: {
      type: String,
      required: true,
    },
    // Titre optionnel affiché en gras
    title: {
      type: String,
      default: '',
    },
    // Description optionnelle affichée sous le titre
    description: {
      type: String,
      default: '',
    },
    // Indique si c'est une adresse suggérée (pour futur usage)
    isSuggested: {
      type: Boolean,
      default: false,
    },
    // Désactive le bouton radio
    disabled: {
      type: Boolean,
      default: false,
    },
    // État du bouton: 'no' (défaut), 'error', etc.
    state: {
      type: String,
      default: 'no',
      validator: (value) => ['no', 'error'].includes(value),
    },
  },
  computed: {
    isSelected() {
      return this.modelValue === this.value;
    },
    // Classes CSS pour le conteneur
    containerClasses() {
      return [
        this.state,
        {
          disabled: this.disabled,
        },
      ];
    },
  },
  methods: {
    handleChange() {
      this.$emit('update:modelValue', this.value);
    },
  },
};
</script>

<style lang="scss" scoped>
.customizable-radio-button {
  --label-font-family: var(--brk-text-body-base-font-family, 'LotoQc Roman VF');
  --label-font-size: var(--brk-text-body-base-font-size, 16px);
  --label-font-weight: var(--brk-text-body-base-font-weight, 405);
  --outline-width: var(--brk-interactive-outline-width, 4px);
  --outline-color-focus: var(--brk-interactive-standard-outline-color-focus, #000233);
  --outline-offset: var(--brk-interactive-outline-offset, 2px);

  position: relative;

  --accent-background-color: #F2F3FF;
  --input-background-color: #fff;
  --input-border-color: #1912A4;
  --label-color: #374151;
  --input-check-color: #6366f1;
}

.customizable-radio-button.disabled {
  --input-background-color: #f5f5f5;
  --input-border-color: #ccc;
  --label-color: #999;
}

.customizable-radio-button.error,
.customizable-radio-button.error input:checked + .radio-label {
  --input-border-color: #bf0000;
}

.customizable-radio-button input:checked + .radio-label {
  --accent-background-color: #CCCEFF;
}

.customizable-radio-button input {
  opacity: 0;
  position: absolute;
}

.customizable-radio-button input:focus {
  outline: 0;
}

.customizable-radio-button input:focus-visible + label {
  outline: var(--outline-width) solid var(--outline-color-focus);
  outline-offset: var(--outline-offset);
}

.customizable-radio-button .radio-label {
  align-items: stretch;
  background: var(--input-background-color);
  border: 1px solid var(--input-border-color);
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  overflow: hidden;
  padding: 8px 8px 8px 0;
  position: relative;
}

/* Bande colorée à gauche */
.customizable-radio-button .accent-band {
  background-color: var(--accent-background-color);
  bottom: 0;
  display: block;
  left: 0;
  position: absolute;
  top: 0;
  width: 50px;
  z-index: 1;
}

/* Cercle radio */
.customizable-radio-button .radio-circle {
  background: var(--input-background-color);
  border: 2px solid var(--input-border-color);
  border-radius: 50%;
  display: block;
  height: 24px;
  left: calc(50px / 2 - 12px);
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  z-index: 2;
}

/* Point de sélection */
.customizable-radio-button .radio-dot {
  background: var(--input-check-color);
  border-radius: 50%;
  height: 12px;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  z-index: 3;
}

/* État sélectionné - bande colorée change */
.customizable-radio-button input:checked + .radio-label .accent-band {
  background-color: var(--accent-background-color);
}

.customizable-radio-button .content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 60px;
  position: relative;
  width: 100%;
  z-index: 2;
}

.customizable-radio-button .content .title {
  color: var(--label-color);
  font-family: var(--label-font-family);
  font-weight: bold;
  line-height: 24px;
}

.customizable-radio-button .content .description {
  color: var(--label-color);
  font-family: var(--label-font-family);
  font-size: var(--label-font-size);
  font-weight: var(--label-font-weight);
  line-height: 24px;
}

.customizable-radio-button .content .slot-content,
.customizable-radio-button .content .slot-default {
  margin-top: 0;
}

.customizable-radio-button .right-container {
  align-self: center;
  display: block;
  min-width: 48px;
}
</style>
